import type { components } from '@/generated/contracts/api-v1';
import { fieldMap, requireId, type FieldSpec, type LegacyRow } from './fieldMap';

// user_feedback table; FeedbackService.ToFeedbackDto in lureo-backend Data/Api/V1/Feedback/FeedbackService.cs
export type UserFeedbackDto = components['schemas']['UserFeedbackDto'];
export type UserFeedbackSource = components['schemas']['UserFeedbackSource'];
export type CreateUserFeedbackRequest = components['schemas']['CreateUserFeedbackRequest'];

// FeedbackService.StoredSources: v1 value -> stored value
const SOURCES = {
  inCms: 'in_cms',
  onExitEditor: 'on_exit_editor',
  inEditor: 'in_editor',
  manualSubmission: 'manual_submission',
  mobile: 'mobile',
  external: 'external',
} as const satisfies Record<UserFeedbackSource, string>;

const spec = {
  uniqueId: 'user_feedback_id',
  userUniqueId: 'user_id',
  // A stored value outside the known list reads as null
  source: { legacy: 'source', as: 'enum', values: SOURCES },
  rating: 'rating',
  question: 'question',
  response: 'response',
  respondedTo: 'responded_to',
  createdAt: 'created_at',
} as const satisfies FieldSpec<UserFeedbackDto>;

const map = fieldMap<UserFeedbackDto, typeof spec>('User feedback', spec);

export type LegacyUserFeedback = LegacyRow<typeof spec, UserFeedbackDto>;

export const userFeedbackToLegacy = (dto: UserFeedbackDto): LegacyUserFeedback => map.toLegacy(dto);

/** createUserFeedback body from a legacy insert row; user_feedback_id is required. The user is the caller. */
export function userFeedbackCreateBody(row: Partial<LegacyUserFeedback>): CreateUserFeedbackRequest {
  const body = map.toBody(row, ['uniqueId', 'source', 'rating', 'question', 'response'], ['user_id', 'created_at']);
  return { ...body, uniqueId: requireId('User feedback', 'user_feedback_id', body.uniqueId) };
}
